import type { ButtonProps as AriaButtonProps, LinkProps } from "react-aria-components";

import type { ReactNode, Ref, RefAttributes } from "react";

import { forwardRef } from "react";

import clsx from "clsx";
import { Button as AriaButton, Link as AriaLink } from "react-aria-components";

import styles from "./button.module.css";

type ButtonVariant = "danger" | "primary" | "secondary" | "tertiary";

type ButtonSize = "md" | "sm";

type ButtonContentProps = {
	children?: AriaButtonProps["children"] | LinkProps["children"];
	isLoading: boolean;
};

function ButtonContent({ children, isLoading }: ButtonContentProps) {
	return (
		<>
			{isLoading && <span className={styles.loader} data-testid="button-spinner" />}
			{children}
		</>
	);
}

export type ButtonProps = (AriaButtonProps | (LinkProps & RefAttributes<HTMLAnchorElement>)) & {
	fullWidth?: boolean;
	iconOnly?: boolean;
	isLoading?: boolean;
	/**
	 * Shows the disabled UI without making the button disabled
	 * This allows i.e. Tooltips to be shown on top of disabled buttons
	 */
	isVisuallyDisabled?: boolean;
	size?: ButtonSize;
	variant?: ButtonVariant;
};

export const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
	function FuseButton(
		{
			children,
			fullWidth,
			iconOnly = false,
			isLoading = false,
			isVisuallyDisabled = false,
			size = "md",
			variant = "primary",
			...props
		},
		ref,
	): ReactNode {
		const genericProps = {
			...props,
			className: clsx(
				props.className,
				"body-2",
				styles[variant],
				styles[size],
				styles.baseButton,
				iconOnly && styles.iconOnly,
				(isLoading || props.isDisabled || isVisuallyDisabled) && styles.loading,
				fullWidth && styles.fullWidth,
			),
			"data-loading": isLoading,
			"data-visually-disabled": isVisuallyDisabled || undefined,
			isDisabled: isLoading || props.isDisabled,
		};

		if ("href" in props) {
			return (
				<AriaLink {...(genericProps as LinkProps)} ref={ref as Ref<HTMLAnchorElement>}>
					<ButtonContent isLoading={isLoading}>{children}</ButtonContent>
				</AriaLink>
			);
		}

		return (
			<AriaButton
				type="button"
				{...(genericProps as AriaButtonProps)}
				ref={ref as Ref<HTMLButtonElement>}
			>
				<ButtonContent isLoading={isLoading}>{children}</ButtonContent>
			</AriaButton>
		);
	},
);
